import type { EngineLocalAiReadiness, OracordEngineAdminCheck } from '@oracord/shared';

type EngineSetupCheckSource = {
  checks?: readonly OracordEngineAdminCheck[] | null;
};

export type CustomerReadyEngineMark = 'Engine installed' | 'Paired' | 'Business setup loaded' | 'Running';
export type CustomerVisibleEngineMark = CustomerReadyEngineMark;

export type EngineSetupStatusItem = {
  mark: CustomerVisibleEngineMark;
  label: string;
  readyLabel: string;
  notReadyLabel: string;
  done: boolean;
};

export type LocalAiSetupState = {
  done: boolean;
  label: 'Set up local AI' | 'Local AI ready' | 'Retry local AI setup';
  action: 'Set up local AI' | 'Local AI ready' | 'Retry local AI setup';
  detail: string;
  statusLabel: string;
  route: '/setup';
  fallbackCommand: '~/.oracord-engine/bin/oracord-engine setup-local-ai';
  command: 'oracord-engine setup-local-ai';
};

const ENGINE_SETUP_STATUS_LABELS: Array<Omit<EngineSetupStatusItem, 'done'>> = [
  { mark: 'Engine installed', label: 'Engine app', readyLabel: 'Installed', notReadyLabel: 'Not installed' },
  { mark: 'Paired', label: 'This Mac', readyLabel: 'Paired', notReadyLabel: 'Not paired' },
  { mark: 'Business setup loaded', label: 'Package status', readyLabel: 'Package loaded', notReadyLabel: 'Package not loaded' },
  { mark: 'Running', label: 'Engine status', readyLabel: 'Running', notReadyLabel: 'Not running' },
];

export function engineSetupCheckDone(engine: EngineSetupCheckSource | null | undefined, label: CustomerReadyEngineMark): boolean {
  return engine?.checks?.some(check => check.label === label && check.status === 'ready') ?? false;
}

export function engineSetupStatusItems(engine: EngineSetupCheckSource | null | undefined): EngineSetupStatusItem[] {
  return ENGINE_SETUP_STATUS_LABELS.map(item => ({ ...item, done: engineSetupCheckDone(engine, item.mark) }));
}

export function describeLocalAiSetup(localAi: EngineLocalAiReadiness | null | undefined): LocalAiSetupState {
  const command = 'oracord-engine setup-local-ai' as const;
  const route = '/setup' as const;
  const fallbackCommand = '~/.oracord-engine/bin/oracord-engine setup-local-ai' as const;
  const base = { command, route, fallbackCommand };
  if (localAi?.smokePassed) {
    return {
      ...base,
      done: true,
      label: 'Local AI ready',
      action: 'Local AI ready',
      statusLabel: `Ready${localAi.model ? ` · ${localAi.model}` : ''}`,
      detail: 'Local AI is ready on this Mac. Gmail, Calendar, speech-to-text, and monitoring are separate setup items.',
    };
  }
  if (localAi?.status === 'failed') {
    return {
      ...base,
      done: false,
      label: 'Retry local AI setup',
      action: 'Retry local AI setup',
      statusLabel: 'Local AI needs attention',
      detail: 'Try again from this page. Oracord will open Engine to finish local AI setup. Use the manual command only if Engine does not open.',
    };
  }
  if (localAi?.status === 'model_ready') {
    return {
      ...base,
      done: false,
      label: 'Set up local AI',
      action: 'Set up local AI',
      statusLabel: 'Finishing check',
      detail: 'The local model appears downloaded. Oracord is waiting for Engine to finish one last check.',
    };
  }
  if (localAi?.status === 'not_configured') {
    return {
      ...base,
      done: false,
      label: 'Set up local AI',
      action: 'Set up local AI',
      statusLabel: 'Waiting for local AI',
      detail: 'Engine will guide local AI setup on this Mac, including the official Ollama Mac app if needed.',
    };
  }
  return {
    ...base,
    done: false,
    label: 'Set up local AI',
    action: 'Set up local AI',
    statusLabel: 'Not set up yet',
    detail: 'Open Engine to set up local AI on this Mac. Engine handles the download and reports back here.',
  };
}
